import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { buildAssistantAppContext, todayRange } from "../lib/assistantContext";
import {
  buildAssistantFoodLogAction,
  buildPendingFoodLogAction,
  createLogPayloadFromFood,
  getActionLanguage,
  resolveFoodClarificationChoice,
} from "../lib/assistantFoodActions";
import { buildAiMealDraft, getAiMealDraftStorageKey, isMealBuilderQuestion } from "../lib/aiMealBuilder";
import { getAssistantIntent, isHistoryIntent } from "../lib/assistantRules";
import { supabase } from "../lib/supabase";

const ASSISTANT_ENDPOINT = "/.netlify/functions/dialysis-assistant";

function localDateKey() {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

function loadDailyUsage(storageKey) {
  if (!storageKey) return 0;

  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || "{}");
    return stored.date === localDateKey() && Number.isInteger(stored.count)
      ? Math.max(0, stored.count)
      : 0;
  } catch {
    localStorage.removeItem(storageKey);
    return 0;
  }
}

function cleanAssistantAnswer(answer) {
  return answer
    .replace(/\*\*([^*\n]+)\*\*/g, "$1")
    .replace(/(^|[\s(])\*([^*\n]+)\*(?=$|[\s).,!?;:])/g, "$1$2")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*[-*]\s+/gm, "- ")
    .replace(/\*/g, "");
}

function readGuestFoodLogs() {
  if (typeof window === "undefined") return [];

  try {
    const saved = localStorage.getItem("guestFoodLogs");
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    localStorage.removeItem("guestFoodLogs");
    return [];
  }
}

function saveGuestFoodLogs(logs) {
  localStorage.setItem("guestFoodLogs", JSON.stringify(logs));
}

function createLocalLogId() {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `ai-log-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatQuantity(value) {
  const number = Number(value || 1);
  return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(1)));
}

function formatIngredientNames(ingredients = []) {
  return ingredients.map((ingredient) => ingredient.name).filter(Boolean).join(", ");
}

function buildTodayHistoryAnswer(question, appContext) {
  if (!isHistoryIntent(question) || !appContext?.todayHistory?.available) return null;

  const language = getActionLanguage(question);
  const items = appContext.todayHistory.items || [];
  const date = appContext.todayHistory.date || appContext.todayDate || localDateKey();

  if (!items.length) {
    return language === "ar"
      ? `لا توجد أطعمة مسجلة اليوم (${date}).`
      : `No foods are logged for today (${date}).`;
  }

  const itemLines = items.map((item) => {
    const quantityText = formatQuantity(item.quantity);
    const ingredients = formatIngredientNames(item.ingredients || []);
    if (language === "ar") {
      return ingredients
        ? `اليوم سجلت ${item.name}. تحتوي على: ${ingredients}.`
        : `اليوم سجلت ${item.name} x ${quantityText}.`;
    }
    return ingredients
      ? `Today you logged ${item.name}. It includes: ${ingredients}.`
      : `Today you logged ${item.name} x ${quantityText}.`;
  });

  return itemLines.join("\n");
}

function buildFoodDatabaseAnswer(question, appContext) {
  if (!appContext?.foodContext?.length) return null;

  const language = getActionLanguage(question);
  const foods = appContext.foodContext
    .map((food) => food.name || food.name_en || food.name_ar)
    .filter(Boolean)
    .slice(0, 8);

  if (!foods.length) return null;

  return language === "ar"
    ? `الأطعمة الموجودة حالياً في قاعدة الطعام تشمل: ${foods.join(", ")}. استخدم حاسبة الوجبة لاختيار طعام وحساب القيم من قاعدة البيانات.`
    : `Foods currently in the database include: ${foods.join(", ")}. Use Meal Mixer to choose foods and calculate values from the database.`;
}

function buildFoodLoadErrorMessage(question) {
  return getActionLanguage(question) === "ar"
    ? "قائمة الطعام غير متاحة مؤقتاً. يرجى المحاولة مرة أخرى بعد قليل أو استخدام حاسبة الوجبة إذا كانت الأطعمة ظاهرة هناك."
    : "The food database is temporarily unavailable. Please try again shortly, or use Meal Mixer if foods are visible there.";
}

function draftCreatedMessage(question, fallbackMessage) {
  return /[\u0600-\u06ff]/.test(question)
    ? "تم إنشاء مسودة وجبة. افتح حاسبة الوجبة لمراجعة المكونات والكميات قبل الحفظ."
    : fallbackMessage;
}

async function loadSafeFoods() {
  const { data, error } = await supabase
    .from("foods")
    .select("id, name, category, serving_size, potassium_mg, phosphorus_mg, sodium_mg, protein_g");

  if (error) throw error;
  return data || [];
}

async function loadTodayFoodLogs(userId) {
  if (!userId) return [];
  const { start, end } = todayRange();
  const { data, error } = await supabase
    .from("food_logs")
    .select("id, food_id, food_name, category, serving_size, quantity, potassium_mg, phosphorus_mg, sodium_mg, protein_g, logged_at, mixed_meal_ingredients")
    .eq("user_id", userId)
    .gte("logged_at", start)
    .lt("logged_at", end)
    .order("logged_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export function useDialysisAssistant() {
  const { user } = useAuth();
  const { t } = useLang();
  const guestMode =
    typeof window !== "undefined" &&
    localStorage.getItem("guestMode") === "true";
  const ownerId = guestMode ? "guest" : user?.id;
  const mealDraftKey = getAiMealDraftStorageKey({ guestMode, userId: user?.id });
  const dailyLimit = guestMode ? 5 : 30;
  const storageKey = ownerId ? `ddp-ai-usage-${ownerId}` : null;
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingLogAction, setPendingLogAction] = useState(null);
  const [pendingFoodClarification, setPendingFoodClarification] = useState(null);
  const [usageCount, setUsageCount] = useState(() => loadDailyUsage(storageKey));

  useEffect(() => {
    setUsageCount(loadDailyUsage(storageKey));
  }, [storageKey]);

  const remaining = useMemo(
    () => Math.max(0, dailyLimit - usageCount),
    [dailyLimit, usageCount]
  );

  const recordUsage = () => {
    const nextUsageCount = loadDailyUsage(storageKey) + 1;
    setUsageCount(nextUsageCount);
    if (storageKey) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ date: localDateKey(), count: nextUsageCount })
      );
    }
  };

  const askAssistant = async (questionOverride) => {
    const trimmedQuestion = (typeof questionOverride === "string" ? questionOverride : question).trim();

    if (!trimmedQuestion) {
      const errorMessage = t("assistant_question_required");
      setError(errorMessage);
      return { ok: false, error: errorMessage };
    }

    if (remaining === 0) {
      const errorMessage = t("assistant_limit_reached");
      setError(errorMessage);
      return { ok: false, error: errorMessage };
    }

    setLoading(true);
    setError("");
    setAnswer("");

    try {
      let foods = [];
      try {
        foods = await loadSafeFoods();
      } catch (foodLoadError) {
        console.error("Assistant food database load failed", foodLoadError);
        const message = buildFoodLoadErrorMessage(trimmedQuestion);
        setPendingLogAction(null);
        setPendingFoodClarification(null);
        setAnswer(message);
        setQuestion("");
        return { ok: false, error: message };
      }

      let registeredTodayLogs = [];
      if (!guestMode && user?.id) {
        try {
          registeredTodayLogs = await loadTodayFoodLogs(user.id);
        } catch {
          registeredTodayLogs = [];
        }
      }

      const appContext = buildAssistantAppContext({
        guestMode,
        userId: user?.id,
        foods,
        registeredTodayLogs,
      });

      const clarificationChoice = resolveFoodClarificationChoice(trimmedQuestion, pendingFoodClarification);
      if (clarificationChoice?.food) {
        const actionResult = buildPendingFoodLogAction({
          food: clarificationChoice.food,
          question: pendingFoodClarification.originalQuestion,
          language: pendingFoodClarification.language,
          todayHistory: appContext.todayHistory,
        });

        if (actionResult) {
          const cleanedAnswer = cleanAssistantAnswer(actionResult.answer);
          setPendingFoodClarification(null);
          setPendingLogAction(actionResult.kind === "pending" ? actionResult.action : null);
          setAnswer(cleanedAnswer);
          setQuestion("");
          recordUsage();
          return {
            ok: true,
            question: trimmedQuestion,
            answer: cleanedAnswer,
            pendingLogAction: actionResult.kind === "pending" ? actionResult.action : null,
          };
        }
      }

      const assistantIntent = getAssistantIntent(trimmedQuestion);

      if (assistantIntent === "food_log_action") {
        const actionResult = buildAssistantFoodLogAction({
          question: trimmedQuestion,
          foods,
          todayHistory: appContext.todayHistory,
        });
        if (actionResult) {
          const cleanedAnswer = cleanAssistantAnswer(actionResult.answer);
          setPendingLogAction(actionResult.kind === "pending" ? actionResult.action : null);
          setPendingFoodClarification(actionResult.kind === "clarification" ? actionResult.clarification : null);
          setAnswer(cleanedAnswer);
          setQuestion("");
          recordUsage();
          return {
            ok: true,
            question: trimmedQuestion,
            answer: cleanedAnswer,
            pendingLogAction: actionResult.kind === "pending" ? actionResult.action : null,
            pendingFoodClarification: actionResult.kind === "clarification" ? actionResult.clarification : null,
          };
        }
      }

      const historyAnswer = buildTodayHistoryAnswer(trimmedQuestion, appContext);
      if (historyAnswer) {
        const cleanedAnswer = cleanAssistantAnswer(historyAnswer);
        setPendingLogAction(null);
        setPendingFoodClarification(null);
        setAnswer(cleanedAnswer);
        setQuestion("");
        recordUsage();
        return { ok: true, question: trimmedQuestion, answer: cleanedAnswer };
      }

      if (assistantIntent === "food_database_question") {
        const foodDatabaseAnswer = buildFoodDatabaseAnswer(trimmedQuestion, appContext);
        if (foodDatabaseAnswer) {
          const cleanedAnswer = cleanAssistantAnswer(foodDatabaseAnswer);
          setPendingLogAction(null);
          setPendingFoodClarification(null);
          setAnswer(cleanedAnswer);
          setQuestion("");
          recordUsage();
          return { ok: true, question: trimmedQuestion, answer: cleanedAnswer };
        }
      }

      let mealBuilder = null;
      if (assistantIntent === "meal_builder" && isMealBuilderQuestion(trimmedQuestion)) {
        mealBuilder = buildAiMealDraft({ question: trimmedQuestion, foods });
      }

      const response = await fetch(ASSISTANT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: trimmedQuestion,
          mealBuilder: mealBuilder?.isMealBuilderRequest ? mealBuilder.context : undefined,
          appContext,
        }),
      });
      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(body.error || t("assistant_unavailable"));
      }

      if (!body.answer) {
        throw new Error(t("assistant_unavailable"));
      }

      const hasDraft = Boolean(mealBuilder?.draft?.ingredients?.length && mealDraftKey);
      const cleanedAnswer = hasDraft
        ? `${cleanAssistantAnswer(body.answer)}\n\n${draftCreatedMessage(trimmedQuestion, t("assistant_meal_draft_created"))}`
        : cleanAssistantAnswer(body.answer);
      if (hasDraft) {
        localStorage.setItem(mealDraftKey, JSON.stringify(mealBuilder.draft));
      }
      setPendingLogAction(null);
      setPendingFoodClarification(null);
      setAnswer(cleanedAnswer);
      setQuestion("");
      recordUsage();
      return { ok: true, question: trimmedQuestion, answer: cleanedAnswer };
    } catch (requestError) {
      const errorMessage = requestError.message || t("assistant_unavailable");
      setError(errorMessage);
      return { ok: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const confirmPendingLogAction = async () => {
    if (!pendingLogAction?.food) {
      return { ok: false, error: t("assistant_log_action_missing") };
    }

    setError("");
    const payload = createLogPayloadFromFood(
      pendingLogAction.food,
      pendingLogAction.quantity || 1,
      new Date().toISOString()
    );

    try {
      if (guestMode) {
        const nextLogs = [
          {
            id: createLocalLogId(),
            ...payload,
            source: "ai_confirmed_action",
          },
          ...readGuestFoodLogs(),
        ];
        saveGuestFoodLogs(nextLogs);
      } else {
        if (!user?.id) throw new Error(t("assistant_unavailable"));
        const { error: insertError } = await supabase.from("food_logs").insert({
          ...payload,
          user_id: user.id,
        });
        if (insertError) throw insertError;
      }

      const message = pendingLogAction.language === "ar"
        ? "تم تسجيله في سجل اليوم."
        : "Logged to today's history.";
      setAnswer(message);
      setPendingLogAction(null);
      setPendingFoodClarification(null);
      return { ok: true, message };
    } catch (confirmError) {
      const errorMessage = confirmError.message || t("assistant_unavailable");
      setError(errorMessage);
      return { ok: false, error: errorMessage };
    }
  };

  const cancelPendingLogAction = () => {
    const message = pendingLogAction?.language === "ar"
      ? "تم الإلغاء. لم يتم تسجيل أي شيء."
      : "Cancelled. Nothing was logged.";
    setPendingLogAction(null);
    setPendingFoodClarification(null);
    setAnswer(message);
    return { ok: true, message };
  };

  return {
    question,
    setQuestion,
    answer,
    error,
    loading,
    usageCount,
    dailyLimit,
    remaining,
    askAssistant,
    pendingLogAction,
    pendingFoodClarification,
    confirmPendingLogAction,
    cancelPendingLogAction,
  };
}
